import React, {
  useState,
  useCallback,
  useEffect,
  useRef,
  memo,
  Suspense,
} from "react";
import { Outlet, useLocation } from "react-router-dom";
import Sidebar from "./Sidebar";
import Header from "./Header";
import ActiveCallWidget from "../calls/ActiveCallWidget";
import WrapUpModal from "../calls/WrapUpModal";
import FrontDeskWrapUpModal from "../calls/FrontDeskWrapUpModal";
import ManualDialerFAB from "../calls/ManualDialerFAB";
import HelpAssistant from "../help/HelpAssistant";
import { useCallState } from "../../context/CallStateContext";
import { useTwilio } from "../../context/TwilioContext";
import { useAuth } from "../../context/AuthContext";
import { useLocation as useAppLocation } from "../../context/LocationContext";
import callService from "../../services/callService";
import smsTemplateService from "../../services/smsTemplateService";
import { stopRingback } from "../../utils/ringbackTone";
import clsx from "clsx";

/** Only the main pane suspends; sidebar/header stay mounted during lazy route loads. */
function OutletLoadingFallback() {
  return (
    <div
      className="flex min-h-[min(360px,55vh)] items-center justify-center py-8"
      aria-busy="true"
      aria-label="Loading page"
      role="status"
    >
      <div className="h-8 w-8 shrink-0 rounded-full border-2 border-brand-400 border-t-transparent animate-spin" />
    </div>
  );
}

const Layout = memo(() => {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const location = useLocation();
  const { user } = useAuth();
  const { currentLocationId } = useAppLocation();
  const wrapUpOrganizationId =
    user?.organizationId?._id || user?.organizationId || "";

  const callState = useCallState();
  const {
    activeCallLead,
    activeTwilioCallSid,
    voicemailUrl,
    setVoicemailUrl,
    isDialerActive,
    showWrapUp,
    setShowWrapUp,
    lastCallStatus,
    setLastCallStatus,
    setActiveCallLead,
    setActiveTwilioCallSid,
    callSource,
    clearCallState,
    pendingWrapUp,
    savePendingWrapUp,
  } = callState;

  /** Matches Lead Queue: skip cancel leg after voicemail drop. */
  const isVoicemailDroppedRef = useRef(false);
  /** Prefetch SMS folders + email templates for global WrapUpModal (front desk / recare / manual dialer). */
  const [globalWrapPrefetchedSms, setGlobalWrapPrefetchedSms] = useState();
  const [globalWrapPrefetchedEmail, setGlobalWrapPrefetchedEmail] = useState();
  const { isInCall, callStatus, setCallStatus, hangUp } = useTwilio();

  const isOnLeadQueue = location.pathname === "/agent/lead-queue";

  // Restore pending wrapup from localStorage on mount (only if not on LeadQueue —
  // LeadQueuePage handles its own restoration)
  useEffect(() => {
    if (isOnLeadQueue) return;
    if (pendingWrapUp && !activeCallLead && !showWrapUp) {
      const restoredEmail = pendingWrapUp.leadEmail || "";
      setActiveCallLead({
        _id: pendingWrapUp.leadId,
        patientId: pendingWrapUp.leadId,
        firstName: pendingWrapUp.leadFirstName || "",
        lastName: pendingWrapUp.leadLastName || "",
        contact: {
          phone: pendingWrapUp.leadPhone,
          ...(restoredEmail ? { email: restoredEmail } : {}),
        },
        phone: pendingWrapUp.leadPhone,
        ...(restoredEmail ? { email: restoredEmail } : {}),
        ...(pendingWrapUp.appointmentId
          ? {
              appointmentId: String(pendingWrapUp.appointmentId),
              ...(pendingWrapUp.appointmentTime
                ? { appointmentTime: pendingWrapUp.appointmentTime }
                : {}),
              ...(pendingWrapUp.appointmentType
                ? { appointmentType: pendingWrapUp.appointmentType }
                : {}),
            }
          : {}),
      });
      setActiveTwilioCallSid(pendingWrapUp.twilioCallSid || null);
      setLastCallStatus(pendingWrapUp.lastCallStatus || "completed");
      setShowWrapUp(true);
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const handleDrawerToggle = useCallback(() => {
    setSidebarOpen((prev) => !prev);
  }, []);

  const handleCloseSidebar = useCallback(() => {
    setSidebarOpen(false);
  }, []);

  const handleCollapseChange = useCallback((collapsed) => {
    setSidebarCollapsed(collapsed);
  }, []);

  // ─── Global call status polling ──────────────────────────────────
  // Mirrors LeadQueuePage's polling so the ActiveCallWidget gets
  // status updates for calls started from ManualDialer.
  const pollingRef = useRef(null);
  const pollCountRef = useRef(0);
  const activeCallLeadRef = useRef(null);
  activeCallLeadRef.current = activeCallLead;
  const activeSidRef = useRef(null);
  activeSidRef.current = activeTwilioCallSid;

  useEffect(() => {
    isVoicemailDroppedRef.current = false;
  }, [activeTwilioCallSid]);

  // Voicemail URL + wrap-up comms (same sources as Lead Queue) for global outbound calls.
  useEffect(() => {
    if (!currentLocationId) {
      setVoicemailUrl(null);
      setGlobalWrapPrefetchedSms(undefined);
      setGlobalWrapPrefetchedEmail(undefined);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const [smsRes, missedRes] = await Promise.allSettled([
          smsTemplateService.getFoldersByLocation(currentLocationId),
          smsTemplateService.getMissedCallTemplates(currentLocationId),
        ]);
        if (cancelled) return;
        let smsFolders = [];
        if (smsRes.status === "fulfilled") {
          const r = smsRes.value;
          smsFolders = r?.data || r || [];
        }
        let missed = [];
        if (missedRes.status === "fulfilled") {
          const r = missedRes.value;
          missed = r?.data || r || [];
        }
        const vms = missed.filter(
          (t) =>
            String(t?.type || "").toLowerCase() === "voicemail" &&
            t.voicemailUrl,
        );
        setVoicemailUrl(vms[0]?.voicemailUrl || null);
        const emails = missed.filter((t) => {
          const hasEmailContent = !!(t?.emailSubject || t?.emailBody);
          const type = String(t?.type || "").toLowerCase();
          const looksLikeEmailType = type === "email";
          const isEnabled = t?.enabled !== false;
          return isEnabled && (looksLikeEmailType || hasEmailContent);
        });
        setGlobalWrapPrefetchedSms(Array.isArray(smsFolders) ? smsFolders : []);
        setGlobalWrapPrefetchedEmail(emails);
      } catch {
        if (!cancelled) {
          setVoicemailUrl(null);
          setGlobalWrapPrefetchedSms([]);
          setGlobalWrapPrefetchedEmail([]);
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [currentLocationId, setVoicemailUrl]);

  const stopPolling = useCallback(() => {
    if (pollingRef.current) {
      clearTimeout(pollingRef.current);
      pollingRef.current = null;
    }
  }, []);

  const startPolling = useCallback(
    (twilioCallSid) => {
      stopPolling();
      pollCountRef.current = 0;

      const poll = async () => {
        pollCountRef.current += 1;
        try {
          const res = await callService.getCallStatusCached(twilioCallSid);
          const raw = res?.data?.status;
          const status =
            typeof raw === "string" ? raw.toLowerCase() : String(raw || "");
          if (status === "in-progress" || status === "answered") {
            setCallStatus("connected");
          } else if (status === "ringing" || status === "queued") {
            setCallStatus("ringing");
          } else if (
            [
              "completed",
              "busy",
              "no-answer",
              "failed",
              "canceled",
              "cancelled",
            ].includes(status)
          ) {
            stopPolling();
            stopRingback();
            hangUp();
            setCallStatus(status);
            const terminalOutcome = isVoicemailDroppedRef.current
              ? "voicemail"
              : status;
            setLastCallStatus(terminalOutcome);
            if (status !== "canceled" && status !== "cancelled") {
              const lead = activeCallLeadRef.current;
              if (lead) {
                const leadEmail = lead.contact?.email || lead.email;
                savePendingWrapUp({
                  leadId: lead.patientId || lead._id,
                  leadFirstName: lead.firstName,
                  leadLastName: lead.lastName,
                  leadPhone: lead.phone || lead.contact?.phone,
                  ...(leadEmail ? { leadEmail } : {}),
                  ...(lead.appointmentId
                    ? {
                        appointmentId: String(lead.appointmentId),
                        ...(lead.appointmentTime
                          ? { appointmentTime: lead.appointmentTime }
                          : {}),
                        ...(lead.appointmentType
                          ? { appointmentType: lead.appointmentType }
                          : {}),
                      }
                    : {}),
                  twilioCallSid,
                  lastCallStatus: terminalOutcome,
                });
              }
              setTimeout(() => setShowWrapUp(true), 500);
            } else {
              setActiveCallLead(null);
              setActiveTwilioCallSid(null);
            }
            return;
          }
        } catch {
          // ignore polling errors
        }
        const delay = pollCountRef.current < 40 ? 500 : 1000;
        pollingRef.current = setTimeout(poll, delay);
      };

      pollingRef.current = setTimeout(poll, 500);
    },
    [
      stopPolling,
      hangUp,
      setCallStatus,
      setLastCallStatus,
      setShowWrapUp,
      setActiveCallLead,
      setActiveTwilioCallSid,
      savePendingWrapUp,
    ],
  );

  // Should Layout handle the call? Yes when:
  // - Not on LeadQueue (global calls always handled here)
  // - OR on LeadQueue but call was started from ManualDialer / front desk dashboard
  const isGlobalOwner =
    !isOnLeadQueue ||
    callSource === "manual-dialer" ||
    callSource === "front-desk" ||
    callSource === "recare";

  // Start polling when a global call becomes active
  useEffect(() => {
    if (
      !isGlobalOwner ||
      !activeTwilioCallSid ||
      !activeCallLead ||
      showWrapUp
    ) {
      stopPolling();
      return;
    }
    setCallStatus("ringing");
    startPolling(activeTwilioCallSid);
    return () => stopPolling();
  }, [activeTwilioCallSid, activeCallLead, isGlobalOwner, showWrapUp]); // eslint-disable-line react-hooks/exhaustive-deps

  // When Twilio Device connects (incoming call accepted), stop polling
  useEffect(() => {
    if (!isGlobalOwner) return;

    const handleCallStarted = () => {
      stopPolling();
      setCallStatus("connected");
    };

    window.addEventListener("twilio-call-started", handleCallStarted);
    return () =>
      window.removeEventListener("twilio-call-started", handleCallStarted);
  }, [isGlobalOwner, stopPolling, setCallStatus]);

  // When Twilio Device call ends, show wrap-up
  useEffect(() => {
    if (!isGlobalOwner) return;

    const handleCallEnded = () => {
      stopPolling();
      const sid = activeSidRef.current;
      if (sid && !isVoicemailDroppedRef.current) {
        callService.cancelCall(sid).catch(() => {});
      }
      const lead = activeCallLeadRef.current;
      if (!lead) {
        setCallStatus("");
        return;
      }
      const finalStatus = isVoicemailDroppedRef.current
        ? "voicemail"
        : callStatus || "completed";
      setLastCallStatus(finalStatus);
      const leadEmail = lead.contact?.email || lead.email;
      savePendingWrapUp({
        leadId: lead.patientId || lead._id,
        leadFirstName: lead.firstName,
        leadLastName: lead.lastName,
        leadPhone: lead.phone || lead.contact?.phone,
        ...(leadEmail ? { leadEmail } : {}),
        ...(lead.appointmentId
          ? {
              appointmentId: String(lead.appointmentId),
              ...(lead.appointmentTime
                ? { appointmentTime: lead.appointmentTime }
                : {}),
              ...(lead.appointmentType
                ? { appointmentType: lead.appointmentType }
                : {}),
            }
          : {}),
        twilioCallSid: activeSidRef.current,
        lastCallStatus: finalStatus,
      });
      setCallStatus("");
      setTimeout(() => setShowWrapUp(true), 300);
    };

    window.addEventListener("twilio-call-ended", handleCallEnded);
    return () =>
      window.removeEventListener("twilio-call-ended", handleCallEnded);
  }, [
    isGlobalOwner,
    callStatus,
    setLastCallStatus,
    setShowWrapUp,
    setCallStatus,
    stopPolling,
    savePendingWrapUp,
  ]);

  // Cleanup polling on unmount
  useEffect(() => () => stopPolling(), [stopPolling]);

  // ─── Global call handlers ────────────────────────────────────────
  const handleGlobalEndCall = useCallback(() => {
    stopPolling();
    hangUp();
    if (activeSidRef.current && !isVoicemailDroppedRef.current) {
      callService.cancelCall(activeSidRef.current).catch(() => {});
    }
    const finalStatus = isVoicemailDroppedRef.current
      ? "voicemail"
      : callStatus || "completed";
    setLastCallStatus(finalStatus);
    setShowWrapUp(true);
    // Persist wrapup so it survives refresh
    const lead = activeCallLeadRef.current;
    if (lead) {
      const leadEmail = lead.contact?.email || lead.email;
      savePendingWrapUp({
        leadId: lead.patientId || lead._id,
        leadFirstName: lead.firstName,
        leadLastName: lead.lastName,
        leadPhone: lead.phone || lead.contact?.phone,
        ...(leadEmail ? { leadEmail } : {}),
        ...(lead.appointmentId
          ? {
              appointmentId: String(lead.appointmentId),
              ...(lead.appointmentTime
                ? { appointmentTime: lead.appointmentTime }
                : {}),
              ...(lead.appointmentType
                ? { appointmentType: lead.appointmentType }
                : {}),
            }
          : {}),
        twilioCallSid: activeSidRef.current,
        lastCallStatus: finalStatus,
      });
    }
  }, [
    hangUp,
    callStatus,
    setLastCallStatus,
    setShowWrapUp,
    stopPolling,
    savePendingWrapUp,
  ]);

  const handleGlobalWrapUpComplete = useCallback(() => {
    setShowWrapUp(false);
    savePendingWrapUp(null);
    clearCallState();
    setCallStatus("");
  }, [setShowWrapUp, clearCallState, setCallStatus, savePendingWrapUp]);

  const handleGlobalWrapUpClose = useCallback(() => {
    setShowWrapUp(false);
    savePendingWrapUp(null);
    clearCallState();
    setCallStatus("");
  }, [setShowWrapUp, clearCallState, setCallStatus, savePendingWrapUp]);

  const shouldShowGlobalWidget = !!(
    activeCallLead &&
    !isDialerActive &&
    !showWrapUp &&
    isGlobalOwner
  );
  const shouldShowGlobalWrapUp = !!(
    showWrapUp &&
    activeCallLead &&
    !isDialerActive &&
    isGlobalOwner
  );

  return (
    <div className="min-h-screen bg-surface-50">
      {/* Subtle background pattern */}
      <div className="fixed inset-0 bg-dots opacity-30 pointer-events-none" />

      {/* Gradient accent in top-right */}
      <div className="fixed top-0 right-0 w-[600px] h-[600px] pointer-events-none">
        <div className="absolute inset-0 bg-gradient-radial from-brand-500/5 via-transparent to-transparent" />
      </div>

      {/* Sidebar */}
      <Sidebar
        open={sidebarOpen}
        onClose={handleCloseSidebar}
        onCollapseChange={handleCollapseChange}
      />

      {/* Main content area */}
      <div
        className={clsx(
          "flex flex-col min-h-screen transition-all duration-300",
          sidebarCollapsed ? "lg:pl-20" : "lg:pl-72",
        )}
      >
        {/* Header */}
        <Header onMenuClick={handleDrawerToggle} />

        {/* Page content */}
        <main className="flex-1 relative">
          <div className="p-4 sm:p-6 lg:p-8">
            <div
              className="opacity-0 animate-fade-in"
              style={{ animationFillMode: "forwards" }}
            >
              <Suspense fallback={<OutletLoadingFallback />}>
                <Outlet />
              </Suspense>
            </div>
          </div>
        </main>
      </div>

      {/* Global Active Call Widget */}
      {shouldShowGlobalWidget && (
        <ActiveCallWidget
          lead={activeCallLead}
          onEndCall={handleGlobalEndCall}
          onCallComplete={() => {}}
          activeTwilioCallSid={activeTwilioCallSid}
          voicemailUrl={voicemailUrl}
          hideVoicemailDrop={callSource === "front-desk"}
          onVoicemailDrop={(started) => {
            isVoicemailDroppedRef.current = !!started;
          }}
        />
      )}

      {/* Global wrap-up: front desk uses a minimal appointment-only modal */}
      {shouldShowGlobalWrapUp && callSource === "front-desk" ? (
        <FrontDeskWrapUpModal
          isOpen={shouldShowGlobalWrapUp}
          onComplete={handleGlobalWrapUpComplete}
          lead={activeCallLead}
          twilioCallSid={activeTwilioCallSid}
        />
      ) : (
        <WrapUpModal
          isOpen={shouldShowGlobalWrapUp}
          onClose={handleGlobalWrapUpClose}
          onComplete={handleGlobalWrapUpComplete}
          onCallAgain={null}
          lead={activeCallLead}
          twilioCallSid={activeTwilioCallSid}
          lastCallStatus={lastCallStatus}
          stages={[]}
          currentStageId=""
          pipelineId=""
          organizationId={wrapUpOrganizationId}
          locationId={currentLocationId ? String(currentLocationId) : undefined}
          prefetchedSmsFolders={
            globalWrapPrefetchedSms !== undefined &&
            globalWrapPrefetchedEmail !== undefined
              ? globalWrapPrefetchedSms
              : undefined
          }
          prefetchedEmailTemplates={
            globalWrapPrefetchedSms !== undefined &&
            globalWrapPrefetchedEmail !== undefined
              ? globalWrapPrefetchedEmail
              : undefined
          }
        />
      )}

      {/* Manual Dialer FAB — globally available for agents */}
      {/* <ManualDialerFAB /> */}

      <HelpAssistant />
    </div>
  );
});

Layout.displayName = "Layout";

export default Layout;
